import {
  Button,
  ConfirmModal,
  CustomPageContainer,
  OverflowTooltip,
  TablePaginationContext,
  UserAvatar,
} from '@/components';
import { DeleteColumn } from '@/components/DeleteColumn/DeleteColumn';
import { PagedTable } from '@/components/PagedTable/PagedTable';
import { ToggleColumn } from '@/components/ToggleColumn';
import { queryClient } from '@/constants';
import { useToggleUserAccess } from '@/hooks/use-toggle-user-access';
import { useToggleUserStatus } from '@/hooks/use-toggle-user-status';
import { ReactElement, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { UserItem } from '../../domain';
import { useDeleteUser, useGetUsers } from '../../queries';
import { UserKey } from '../../queries/types';
import { CreateUserModal } from './components/CreateUserModal';
import { getUsersColumnsNamedMapped } from './utils';

export const UsersPage = (): ReactElement => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [userSelected, setUserSelected] = useState<number>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedToggleRowId, setSelectedToggleRowId] = useState<number>();

  const onChangeSettled = useCallback(
    () => setSelectedToggleRowId(undefined),
    [],
  );

  const { onChange: onAccessChange, isLoading: isLoadingAccessChange } =
    useToggleUserAccess({
      onSettled: onChangeSettled,
    });
  const { onChange: onStatusChange, isLoading: isLoadingToggleUserStatus } =
    useToggleUserStatus({ onSettled: onChangeSettled });

  const navigate = useNavigate();

  const { mutate: deleteUser, isLoading: isLoadingDelete } = useDeleteUser();

  const { searchTerm, page, tableSort } = useContext(TablePaginationContext);

  const { data: users, isLoading: isLoadingUsers } = useGetUsers(
    page,
    searchTerm,
    getUsersColumnsNamedMapped(tableSort?.[0]?.columnId ?? 'name'),
    tableSort?.[0]?.direction ?? 'ascending',
  );

  const handleModalState = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  const handleOnNameClick = useCallback(
    (row: UserItem) => {
      const path = `${row.id}`;
      navigate(path);
    },
    [navigate],
  );

  const handleOpenRemoveUserModalState = useCallback((id: number) => {
    setIsOpenDelete(true);
    setUserSelected(id);
  }, []);

  const handleCloseRemoveUserModalState = useCallback(() => {
    setIsOpenDelete(false);
    setUserSelected(undefined);
  }, []);

  const handleOnAccessChange = useCallback(
    (checked: boolean, row: UserItem) => {
      setSelectedToggleRowId(row.id);
      onAccessChange(checked, +row.id);
    },
    [onAccessChange],
  );

  const handleOnStatusChange = useCallback(
    (checked: boolean, row: UserItem) => {
      setSelectedToggleRowId(row.id);
      onStatusChange(checked, row.id);
    },
    [onStatusChange],
  );

  const handleDeleteUser = useCallback(() => {
    if (!userSelected) {
      return;
    }

    deleteUser(userSelected, {
      onError: () => {
        toast.error(t('error_deleting_user'));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(UserKey.ALL);
        toast.success(t('success_deleting_user'));
      },
    });

    handleCloseRemoveUserModalState();
  }, [userSelected, deleteUser, handleCloseRemoveUserModalState, t]);

  return (
    <CustomPageContainer>
      <CustomPageContainer.Header title={t('users')} />
      <PagedTable
        title={t('users')}
        actions={
          <Button
            onClick={handleModalState}
            variant="primary"
            className="px-5 py-2.5"
          >
            {t('add')}
          </Button>
        }
        columns={[
          {
            id: 'name',
            name: t('name'),
            accessor: 'fullName',
            alignment: 'left',
            renderRow: (row) => (
              <div
                className="flex items-center gap-2.5 overflow-hidden"
                onClick={() => handleOnNameClick(row)}
              >
                <UserAvatar
                  size="small"
                  name={row.fullName}
                  imageId={row.profileImageFileId}
                />
                <OverflowTooltip
                  text={row.fullName}
                  textClassName="font-black"
                />
              </div>
            ),
          },
          {
            id: 'email',
            name: t('email'),
            accessor: 'email',
          },
          {
            id: 'access',
            name: t('access'),
            accessor: 'access',
            renderRow: (row) => (
              <ToggleColumn
                onChange={handleOnAccessChange}
                value={row.access}
                row={row}
                disabled={
                  row.id === selectedToggleRowId && isLoadingAccessChange
                }
              />
            ),
          },
          {
            id: 'status',
            name: t('status'),
            accessor: 'status',
            renderRow: (row) => (
              <ToggleColumn
                onChange={handleOnStatusChange}
                value={row.status}
                row={row}
                disabled={
                  row.id === selectedToggleRowId && isLoadingToggleUserStatus
                }
              />
            ),
          },
          {
            id: 'actions',
            name: t('actions'),
            sortable: false,
            renderRow: (row) => (
              <DeleteColumn
                disabledMessage={t('delete_user')}
                disabled={!row.canDelete || isLoadingDelete}
                onClick={() => handleOpenRemoveUserModalState(row.id)}
              />
            ),
          },
        ]}
        page={users}
        isLoading={isLoadingUsers || isLoadingDelete}
        emptyStateMessage={t('no_users_available')}
      />
      {isOpen && (
        <CreateUserModal handleClose={handleModalState} isOpen={isOpen} />
      )}
      <ConfirmModal
        isOpen={isOpenDelete}
        handleClose={handleCloseRemoveUserModalState}
        title={t('please_confirm_action')}
        message={t('confirm_user_delete')}
        onConfirm={handleDeleteUser}
      />
    </CustomPageContainer>
  );
};
